import * as React from 'react';
import MyTypes from 'MyTypes';
import { connect, ConnectedProps } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { Card, Col, Modal, Button, Form, Table, Spinner } from 'react-bootstrap'; 
import FeeScheduleBuilder from './FeeScheduleBuilder';
import { financialsActions } from 'store/financials';
import _ from 'lodash'; 
import NumberFormat from 'react-number-format'; 
import dateFormat from 'dateformat';
import * as models from '../../models';
import services from 'services';
import utils from '../../utils';
import { userActions } from 'store/user';
import { AppToastTypes } from 'models/enums';

interface Props extends PropsFromRedux {
    claimId: number; 
    claimNumber: string;     
    showModal: boolean;    
    closeModal: () => void;
}

interface State {    
    feeScheduleId: number, 
    invoiceId: number, 
    selectedInvoice: string; 
    showInvoice: boolean; 
    showFSBModal: boolean; 
    zeroChangePhrase: string; 
}

interface Diff {fieldName: string, oldValue: string, newValue: string}; 
interface FeeDiff {operation: string, oldValue: models.InvoiceFee, newValue: models.InvoiceFee}; 

const UsDollar = new Intl.NumberFormat('en-US', {
    style: 'currency', 
    currency: 'USD', 
});  
class InvoiceHistoryModal extends React.Component<Props, State> {
    public state = {
        feeScheduleId: 0, 
        invoiceId: 0, 
        selectedInvoice: "",
        showInvoice: false,  
        showFSBModal: false, 
        zeroChangePhrase: 'No changes detected'
    };

    componentDidUpdate(prevProps: any) {     
        if (!_.isEqual(prevProps.showModal, this.props.showModal) && this.props.showModal) {
            this.getFormData(); 
        }
    }
    
    public getFormData = async () => {
        this.props.requestInvoiceEvents(this.props.claimId); 
    }    

    public resetFormStateAndClose = () => {
        this.props.clearInvoiceEvents(); 
        this.props.closeModal(); 
    }

    public submitForm = (e:any) => {
        e.preventDefault();
        this.resetFormStateAndClose(); 
    }

    public handleSelectInvoice = (e:any) => {                
        this.setState({selectedInvoice: e.target.value}); 
    }

    public formatDiff = (d: Diff) => {       
        console.log(d); 
        switch(d.fieldName.toLocaleLowerCase()) {
            case 'grossloss': 
            case 'grandtotal': 
                return this.getAuditDisplayNames(d.fieldName) + ' changed from ' 
                    + UsDollar.format(parseFloat(d.oldValue)) + ' to ' 
                    + UsDollar.format(parseFloat(d.newValue)); 
            default: 
                return this.getAuditDisplayNames(d.fieldName) + ' changed from ' + d.oldValue + ' to ' + d.newValue;  
        }
    }

    public formatFeeDiff = (d: FeeDiff) => {     
        switch(d.operation.toLocaleLowerCase()){
            case 'fee added':                
                return d.newValue.feeName + ' fee added:'
                    + ' Qty: ' + d.newValue.quantity.toString() + ';'
                    + ' Rate: ' + UsDollar.format(d.newValue.rate) + ';'
                    + ' Total: ' + UsDollar.format(d.newValue.total) + ';'
                    + ' Adj %: ' + d.newValue.adjusterPercent.toFixed(0).toString()
                    + '\n'; 
            case 'fee removed':
                return d.oldValue.feeName + ' fee removed:'
                    + ' Qty: ' + d.oldValue.quantity.toString() + ';'
                    + ' Rate: ' + UsDollar.format(d.oldValue.rate) + ';'
                    + ' Total: ' + UsDollar.format(d.oldValue.total)
                    + '\n'; 
            case 'fee changed':
                const qtyChanged = d.newValue.quantity !== d.oldValue.quantity; 
                const rateChanged = d.newValue.rate !== d.oldValue.rate; 
                const adjPerChanged = d.newValue.adjusterPercent !== d.oldValue.adjusterPercent; 
                const commentChanged = d.newValue.comment !== d.oldValue.comment; 
                const changesDetected = [qtyChanged,rateChanged,adjPerChanged,commentChanged].filter(Boolean).length;  
                const changeText = changesDetected > 1 ? 'changes' : 'change'; 

                let outputString = ''; 
                if(changesDetected === 0)
                    return null; 
                
                outputString += d.newValue.feeName + ` fee updated (${changesDetected.toString()} ${changeText} found):`; 
                outputString += !qtyChanged ? '' : `\n\tQty changed from ${d.oldValue.quantity} to ${d.newValue.quantity}`; 
                outputString += !rateChanged ? '' :  `\n\tRate changed from ${UsDollar.format(d.oldValue.rate)} to ${UsDollar.format(d.newValue.rate)}`; 
                outputString += !adjPerChanged ? '' : `\n\tAdj % changed from ${d.oldValue.adjusterPercent} to ${d.newValue.adjusterPercent}`;  
                if (commentChanged){
                    if(d.oldValue.comment == null)
                        outputString += !commentChanged ? '' : `\n\tComment added ${d.newValue.comment}`;  
                    else 
                        outputString += !commentChanged ? '' : `\n\tComment changed from ${d.oldValue.comment} to ${d.newValue.comment}`;  
                }                    
                return outputString + '\n'; 
                                
            default: 
                return null; 
        }        
    }

    //helpers
    public getAuditDisplayNames = (fieldName: string) => {
        switch (fieldName) {
            case "adjuster": 
                return "Adjuster"; 
            case "comments": 
                return "Comments"; 
            case "feeSchedule":
                return "Fee Schedule";             
            case "grossLoss": 
                return "Gross Loss"; 
            case "grandTotal": 
                return "Grand Total"; 
            case "hours": 
                return "Hours"; 
            case "invoiceGroup": 
                return "Invoice Group"; 
            case "outstandingBalance": 
                return "Outstanding Balance"; 
            case "salesTax": 
                return "Sales Tax";  
            case "subTotal": 
                return "Sub Total"; 
            case "taxRate": 
                return "Tax Rate"; 
            default: 
                return ""; 
        }        
    }

    public getEventDetails = (id: number, type: string, details: any) => {
        if(!details) {
            return <td></td>
        }
        switch (type.toLocaleLowerCase()) {            
            case 'created': 
                const invoice: models.Invoice = details; 
                if(!invoice)
                    return <td></td>
                return <td style={{whiteSpace: "pre"}} >{'Gross Loss: ' + UsDollar.format(invoice.grossLoss)}</td>
            case 'deleted': 
                return <td
                            style={{cursor:"pointer", whiteSpace:"pre-line"}} 
                            className='value-link' 
                            onClick={() => this.viewInvoice(id)}
                            >{details}
                        </td>;             
            case 'fees changed':                                          
                const feeDiffs: FeeDiff[] = details; 
                if(!feeDiffs)
                    return <td>{this.state.zeroChangePhrase}</td>
                const text = feeDiffs.map((d) => (this.formatFeeDiff(d))).filter(x => x !== null);
                if(text.length === 0) return <td>{this.state.zeroChangePhrase}</td>                
                return <td style={{whiteSpace: "pre"}} >{text}</td>
            case 'edited':         
                if (Array.isArray(details)) {                    
                    const feeDiffs: FeeDiff[] = details; 
                    if(!feeDiffs)
                        return <td>{this.state.zeroChangePhrase}</td>
                    const text = feeDiffs.map((d) => (this.formatFeeDiff(d))).filter(x => x !== null);
                    if(text.length === 0) return <td>{this.state.zeroChangePhrase}</td>                
                    return <td style={{whiteSpace: "pre"}} >{text}</td>
                }
                else {
                    const diffs: Diff[] = details; 
                    if(!diffs)
                        return <td>{this.state.zeroChangePhrase}</td>
                    return <td style={{whiteSpace: "pre"}} >{diffs.map((d) => (this.formatDiff(d))).join('\n')}</td>
                }
            case 'fee schedule applied':                
                const fsEvent: {id:number, name: string} = details;
                return <td 
                            style={{cursor:"pointer", whiteSpace:"pre-line"}} 
                            className='value-link' 
                            onClick={() => this.toggleFSBModal(fsEvent.id)}
                            >{fsEvent.name}
                        </td>; 
            case 'pdf created':
                const pdfEvent: {id:number, name: string} = details;                                
                if(!pdfEvent) return <td></td>
                return <td
                            style={{cursor:"pointer"}} 
                            className='value-link' 
                            onClick={() => this.downloadFile(id, pdfEvent.id)}
                            >{pdfEvent.name}
                        </td>
            case 'correction applied':
            case 'payment received':
                return <td>{details}</td>; 
            default: 
                return <td></td>
        }        
    }

    public toggleFSBModal = (feeSchduleId?: number) => {
        this.setState({feeScheduleId: feeSchduleId || 0, showFSBModal: !this.state.showFSBModal}); 
    }

    public toggleInvoice = () => {
        this.setState({showInvoice: !this.state.showInvoice}); 
    }

    public viewInvoice = (id: number) => {
        this.setState({invoiceId: id}); 
        this.toggleInvoice(); 
    }

    public downloadFile = async (id: number, attachmentId: number) => {
        const response = await services.api.financials.getInvoiceAttachment2(id, attachmentId);        
        if(response.isError) {
            this.props.setAppToast({message: "Error retrieving file", type: AppToastTypes.Failure}); 
        }
        else {            
            utils.filehelpers.openFileNewTab(response.data); 
        }
    }
 
    public render() {
        const { claim, closeModal, invoiceEventsResponse, isInvoiceEventsLoading, showModal } = this.props;
        const { feeScheduleId, invoiceId, selectedInvoice, showFSBModal, showInvoice } = this.state; 
        const invoiceEvents = invoiceEventsResponse.resourceList.filter(x => (selectedInvoice || 'All') === 'All' || x.invoiceNumber === selectedInvoice); 

        const invoiceList = [... new Set(invoiceEventsResponse.resourceList.map(x => x.invoiceNumber))];      

        return (
            <>            
            <FeeScheduleBuilder
                company={claim.insuranceCompany}
                feeScheduleId={feeScheduleId}
                showModal={showFSBModal}
                closeModal={this.toggleFSBModal}
            /> 
            <Modal size='xl' show={showModal} onHide={() => this.resetFormStateAndClose()} >                                
                <Modal.Header closeButton>
                    <Modal.Title>Invoice History
                        {isInvoiceEventsLoading && <span>&nbsp;&nbsp;<Spinner animation="border" role="status" variant="primary" size="sm"/></span>}
                    </Modal.Title>
                </Modal.Header>
                    <Modal.Body>
                        <Form className="claim-modal">
                            <Form.Group controlId="invoices" className="input-field-container2">
                                <Col sm={{span: 2}}>
                                    <Form.Label>Invoice #:</Form.Label>
                                </Col>
                                <Col sm={{span:4}}>
                                    <Form.Select name="invoices" 
                                        value={selectedInvoice} 
                                        onChange={(e) => this.handleSelectInvoice(e)}
                                        >
                                            {<option key={-1}>All</option>}                                        
                                            {invoiceList.sort().map((f: string, i: number) => ( 
                                                <option key={i} value={f} >{f}</option>
                                            ))}
                                    </Form.Select>
                                </Col>
                            </Form.Group>   
                        </Form>                        
                        <Card className="claim-card">
                            <Card.Title>Invoice Events</Card.Title>
                            <Card.Body style={{maxHeight: "500px", overflow: "auto" }}>
                                <Table striped>
                                    <thead>
                                        <tr>
                                            <th>Invoice #</th>
                                            <th>User</th>
                                            <th>Date</th>
                                            <th>Event</th>
                                            <th>Total</th>
                                            <th>Payment</th>
                                            <th>Total Paid</th>
                                            <th>Balance</th>
                                            <th>Details</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                    {invoiceEvents && invoiceEvents.map((x: models.InvoiceEvent, i: number) => (
                                        <tr key={i}>
                                            <td>{x.invoiceNumber}</td>
                                            <td>{x.actor}</td>
                                            <td>{dateFormat(x.eventDate, "paddedShortDate")}</td>
                                            <td>{x.eventName}</td>
                                            <td><NumberFormat value={x.grandTotal.toFixed(2)} displayType={'text'} thousandSeparator={true} prefix={'$'} /> </td>
                                            <td><NumberFormat value={x.paymentAmount.toFixed(2)} displayType={'text'} thousandSeparator={true} prefix={'$'} /> </td>
                                            <td><NumberFormat value={x.paymentsReceived.toFixed(2)} displayType={'text'} thousandSeparator={true} prefix={'$'} /> </td>
                                            <td><NumberFormat value={x.outstandingBalance.toFixed(2)} displayType={'text'} thousandSeparator={true} prefix={'$'} /> </td>
                                            {this.getEventDetails(x.invoiceId, x.eventName, x.eventDetails)}
                                        </tr>
                                    ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={this.resetFormStateAndClose}>
                            EXIT
                        </Button>
                    </Modal.Footer>
            </Modal>  
            </>          
        );
    }
}

const mapStateToProps = (state: MyTypes.RootState) => ({
    claim: state.claims.claim.claim, 
    invoiceEventsResponse: state.financials.financials.invoiceEventsResponse, 
    isInvoiceEventsLoading: state.financials.financials.isInvoiceEventsLoading,     
});

const mapDispatchToProps = (dispatch: Dispatch) =>
    bindActionCreators(
        {         
            clearInvoiceEvents: financialsActions.clearInvoiceEvents, 
            requestInvoiceEvents: financialsActions.requestInvoiceEvents, 
            setAppToast: userActions.setAppToast, 
        },
        dispatch
    );

const connector = connect(mapStateToProps, mapDispatchToProps); 
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(InvoiceHistoryModal);